import _ from 'lodash';
import React, {useContext, useState, useEffect, useCallback} from 'react';
import {local as safeStorage} from 'utils/safe-storage';
import BannerEventContext from 'contexts/BannerEventContext';
import Banner, {BannerEventData} from './common/Banner';
import {getSettings} from 'utils';
import LocaleContext from 'contexts/LocaleContext';
import {BannerConfig} from 'components/calculator/config';
import {getEventUrl} from 'components/Community/common/utils';
import RouterContext from 'contexts/RouterContext';
import {createLogger} from 'logger';

const BANNER_HEIGHT = 32;
const CUSTOM_BANNER_EVENT_ID = 'custom';

const logger = createLogger('BannerEvent');

export interface BannerEventProps {
    className?: string;
}

const getBannerConfig = _.memoize(async (localeCode: string) =>
    getSettings<BannerConfig>('banner', localeCode),
);

const checkCustomBannerVisibility = ({start, end}: BannerConfig) => {
    const now = Number(new Date());

    return Number(new Date(start)) < now && now < Number(new Date(end));
};

const BannerEvent: React.FC<BannerEventProps> = ({className}) => {
    const events = useContext(BannerEventContext);
    const locale = useContext(LocaleContext);
    const router = useContext(RouterContext);
    const [activeEvents, setActiveEvents] = useState<BannerEventData[]>([]);
    const [closed, setClosed] = useState(false);

    const event = activeEvents[0];
    const isEventBannerExist = event?.id && !closed;

    const onClose = useCallback(() => {
        setClosed(true);

        const id = event?.id;
        const storageEvents = safeStorage.getItem('closedBannerEvents');
        const closedBannerEvents = `${storageEvents ? `${storageEvents},` : ''}${id}`;

        safeStorage.setItem('closedBannerEvents', closedBannerEvents);
    }, [event?.id, setClosed]);

    //TODO перевести на userSettings https://st.yandex-team.ru/CLOUDFRONT-6944
    useEffect(() => {
        async function setEvents() {
            const customBannerEvents = [];
            const storageClosedBannerEvents =
                safeStorage.getItem('closedBannerEvents')?.split(',') ?? [];

            try {
                const bannerConfig = await getBannerConfig(locale?.code);

                if (
                    bannerConfig &&
                    !bannerConfig.ignorePages?.includes(router.pathname) &&
                    checkCustomBannerVisibility(bannerConfig)
                ) {
                    customBannerEvents.push({
                        id: CUSTOM_BANNER_EVENT_ID,
                        text: bannerConfig.text,
                        url: bannerConfig.url,
                    });
                }
            } catch (error) {
                logger.warn(error as string);
            }

            const defaultEvents = (events ?? []).map(({id, name, url}) => ({
                id,
                text: name,
                url: getEventUrl(id, url),
            }));

            const filteredEvents = [...customBannerEvents, ...defaultEvents].filter(
                (parsedEvent) => !storageClosedBannerEvents.includes(String(parsedEvent.id)),
            );

            setActiveEvents(filteredEvents as BannerEventData[]);
        }
        setEvents();
    }, [events, locale?.code, router.pathname]);

    useEffect(() => setClosed(false), [router.pathname]);

    useEffect(() => {
        document.body.style.setProperty(
            '--banner-event-height',
            isEventBannerExist ? `${BANNER_HEIGHT}px` : null,
        );
    }, [isEventBannerExist]);

    if (!isEventBannerExist) {
        return null;
    }

    return (
        <Banner
            event={event}
            className={className}
            onClose={onClose}
            theme={event.id === CUSTOM_BANNER_EVENT_ID ? 'custom' : 'default'}
        />
    );
};

export default BannerEvent;
